import { useNavigate, useLocation } from "react-router-dom";
import {
  Home, Users, Calendar, LogOut, Bell, Clock,
  UtensilsCrossed, Sparkles, BedDouble, CalendarDays, Settings, ChevronDown,
  TrendingUp, BarChart3, Map, UserCheck, FileText, DoorOpen, CreditCard,
  Tag, Activity, ArrowUpCircle, Package, ChefHat, UserCog,
  ClipboardCheck, Moon, Plus, History, Wrench, Wine, Sun, MessageSquare,
} from "lucide-react";
import { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import "./Sidebar.css";

const ROLE_META: Record<string, { label: string; color: string; bg: string }> = {
  admin:                { label: "Admin",              color: "#f87171", bg: "rgba(239,68,68,0.18)" },
  manager:              { label: "Manager",            color: "#60a5fa", bg: "rgba(37,99,235,0.18)" },
  frontdesk:            { label: "Front Desk",         color: "#34d399", bg: "rgba(52,211,153,0.18)" },
  reception:            { label: "Reception",          color: "#34d399", bg: "rgba(52,211,153,0.18)" },
  room_service:         { label: "Room Service",       color: "#fbbf24", bg: "rgba(251,191,36,0.18)" },
  room_service_manager: { label: "RS Manager",         color: "#fb923c", bg: "rgba(251,146,60,0.18)" },
  spa:                  { label: "Spa",                color: "#a78bfa", bg: "rgba(167,139,250,0.18)" },
  spa_manager:          { label: "Spa Manager",        color: "#c084fc", bg: "rgba(192,132,252,0.18)" },
  housekeeper:          { label: "Housekeeper",        color: "#38bdf8", bg: "rgba(56,189,248,0.18)" },
  housekeeper_manager:  { label: "HK Manager",         color: "#22d3ee", bg: "rgba(34,211,238,0.18)" },
  livechat:             { label: "Live Chat",           color: "#a78bfa", bg: "rgba(167,139,250,0.18)" },
};

interface MenuItem {
  name: string;
  icon: any;
  path: string;
  section?: string;
  children?: MenuItem[];
}

interface SidebarProps {
  drawerOpen?: boolean;
  onClose?: () => void;
}

export default function Sidebar({ drawerOpen, onClose }: SidebarProps) {
  const navigate  = useNavigate();
  const location  = useLocation();

  const [role, setRole]       = useState<string | null>(null);
  const [name, setName]       = useState<string>("");
  const [hotelId, setHotelId] = useState<string | null>(null);
  const [logo, setLogo]       = useState<string | null>(null);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const token = localStorage.getItem("ai2_token");
    if (!token) return;
    try {
      const d: any = jwtDecode(token);
      setRole(d.role);
      setName(d.name || "");
      setHotelId(d.hotelId || d.hotelClusterName || null);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!hotelId) return;
    const token = localStorage.getItem("ai2_token");
    axios
      .get(`${import.meta.env.VITE_API_URL ?? "http://localhost:5050"}/api/hotel/settings/${hotelId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const d = res.data;
        setLogo(d.logoURL || d.logo_url || d.logourl || d.logo || d.brand_logo || null);
      })
      .catch(() => {});
  }, [hotelId]);

  // Auto-open the group that contains the active path
  useEffect(() => {
    const fullPath = location.pathname + location.search;
    const allMenus = [
      ...(role ? (menus[role] || []) : []),
    ];
    const newOpen: Record<string, boolean> = {};
    for (const item of allMenus) {
      if (item.children?.some(c =>
        c.path === fullPath ||
        c.path === location.pathname ||
        (c.path.includes("?") ? fullPath === c.path : location.pathname === c.path)
      )) {
        newOpen[item.name] = true;
      }
    }
    if (Object.keys(newOpen).length) {
      setOpenMenus(o => ({ ...o, ...newOpen }));
    }
  }, [location.pathname, location.search, role]);

  const logout = () => { localStorage.clear(); navigate("/dashboard/login"); };

  const go = (path: string) => {
    navigate(path);
    onClose?.();
  };

  const isActive = (path: string) =>
    path.includes("?")
      ? location.pathname + location.search === path
      : location.pathname === path && !location.search;

  // ── Role menus ────────────────────────────────────────────────
  const menus: Record<string, MenuItem[]> = {

    /* ── ADMIN / MANAGER ── */
    admin: [
      { name: "Overview",      icon: Home,  path: "/dashboard/manager",               section: "Main" },
      { name: "Notifications", icon: Bell,  path: "/dashboard/manager/notifications" },

      {
        name: "Front Office", icon: ArrowUpCircle, path: "/dashboard/manager/frontdesk", section: "Operations",
        children: [
          { name: "Walk-in Booking",  icon: DoorOpen,      path: "/dashboard/manager/frontdesk" },
          { name: "Check In / Out",   icon: ClipboardCheck, path: "/dashboard/manager/checkinout" },
          { name: "Guests",           icon: Users,          path: "/dashboard/manager/guests" },
          { name: "Bookings",         icon: Calendar,       path: "/dashboard/manager/bookings" },
          { name: "Booking Calendar", icon: CalendarDays,   path: "/dashboard/manager/booking-calendar" },
          { name: "Payments",         icon: CreditCard,     path: "/dashboard/manager/payments" },
          { name: "Pre Check-in",     icon: FileText,       path: "/dashboard/manager/pre-checkin" },
        ],
      },

      {
        name: "Rooms", icon: BedDouble, path: "/dashboard/manager/rooms",
        children: [
          { name: "Room List",     icon: BedDouble,    path: "/dashboard/manager/rooms" },
          { name: "Room Calendar", icon: CalendarDays, path: "/dashboard/manager/rooms?tab=calendar" },
          { name: "In House",      icon: Users,        path: "/dashboard/manager/rooms?tab=inhouse" },
          { name: "Room Rates",    icon: Tag,          path: "/dashboard/manager/rooms?tab=rates" },
          { name: "Pricing",       icon: Tag,          path: "/dashboard/manager/pricing-config" },
        ],
      },

      {
        name: "Operations", icon: ClipboardCheck, path: "/dashboard/manager/morning-briefing",
        children: [
          { name: "Morning Briefing", icon: Sun,         path: "/dashboard/manager/morning-briefing" },
          { name: "Housekeeping",     icon: ClipboardCheck, path: "/dashboard/manager/housekeeping" },
          { name: "Minibar",          icon: Wine,        path: "/dashboard/manager/minibar" },
          { name: "Maintenance",      icon: Wrench,      path: "/dashboard/manager/maintenance" },
          { name: "Night Audit",      icon: Moon,        path: "/dashboard/manager/night-audit" },
          { name: "Settlement",       icon: CreditCard,  path: "/dashboard/manager/settlement" },
          { name: "Shift Handover",   icon: FileText,    path: "/dashboard/manager/shift-handover" },
          { name: "Guest Journey",    icon: Map,         path: "/dashboard/manager/guest-journey" },
        ],
      },

      {
        name: "Live Chat", icon: MessageSquare, path: "/dashboard/manager/chat", section: "Messaging",
        children: [
          { name: "Live Chat",      icon: MessageSquare, path: "/dashboard/manager/chat" },
          { name: "Chat Analytics", icon: BarChart3,     path: "/dashboard/manager/chat-analytics" },
        ],
      },

      {
        name: "Analytics", icon: BarChart3, path: "/dashboard/manager/revenue", section: "Analytics",
        children: [
          { name: "Revenue",           icon: TrendingUp, path: "/dashboard/manager/revenue" },
          { name: "Occupancy",         icon: BedDouble,  path: "/dashboard/manager/occupancy" },
          { name: "Advanced KPIs",     icon: Activity,   path: "/dashboard/manager/analytics" },
          { name: "Guest Analytics",   icon: UserCheck,  path: "/dashboard/manager/guest-analytics" },
          { name: "Channel Analytics", icon: BarChart3,    path: "/dashboard/manager/channel-analytics" },
          { name: "Dynamic Pricing",   icon: TrendingUp,  path: "/dashboard/manager/pricing" },
          { name: "Message Metrics",   icon: MessageSquare, path: "/dashboard/manager/message-metrics" },
        ],
      },

      {
        name: "Staff", icon: UserCog, path: "/dashboard/manager/staff-attendance", section: "Staff & Settings",
        children: [
          { name: "Staff Attendance", icon: Clock,   path: "/dashboard/manager/staff-attendance" },
          { name: "Staff Users",      icon: UserCog, path: "/dashboard/manager/users" },
        ],
      },

      { name: "Settings", icon: Settings, path: "/dashboard/manager/settings" },
    ],

    /* ── FRONT DESK / RECEPTION ── */
    frontdesk: [
      { name: "Overview",      icon: Home, path: "/dashboard/manager",               section: "Main" },
      { name: "Notifications", icon: Bell, path: "/dashboard/manager/notifications" },

      {
        name: "Front Office", icon: ArrowUpCircle, path: "/dashboard/manager/frontdesk", section: "Operations",
        children: [
          { name: "Walk-in Booking",  icon: DoorOpen,       path: "/dashboard/manager/frontdesk" },
          { name: "Check In / Out",   icon: ClipboardCheck, path: "/dashboard/manager/checkinout" },
          { name: "Guests",           icon: Users,          path: "/dashboard/manager/guests" },
          { name: "Bookings",         icon: Calendar,       path: "/dashboard/manager/bookings" },
          { name: "Payments",         icon: CreditCard,     path: "/dashboard/manager/payments" },
          { name: "Pre Check-in",     icon: FileText,       path: "/dashboard/manager/pre-checkin" },
        ],
      },

      {
        name: "Rooms", icon: BedDouble, path: "/dashboard/manager/rooms",
        children: [
          { name: "Room List",     icon: BedDouble,    path: "/dashboard/manager/rooms" },
          { name: "Room Calendar", icon: CalendarDays, path: "/dashboard/manager/rooms?tab=calendar" },
          { name: "In House",      icon: Users,        path: "/dashboard/manager/rooms?tab=inhouse" },
          { name: "Room Rates",    icon: Tag,          path: "/dashboard/manager/rooms?tab=rates" },
          { name: "Pricing",       icon: Tag,          path: "/dashboard/manager/pricing-config" },
        ],
      },

      {
        name: "Operations", icon: ClipboardCheck, path: "/dashboard/manager/morning-briefing",
        children: [
          { name: "Morning Briefing", icon: Sun,            path: "/dashboard/manager/morning-briefing" },
          { name: "Housekeeping",     icon: ClipboardCheck, path: "/dashboard/manager/housekeeping" },
          { name: "Night Audit",      icon: Moon,           path: "/dashboard/manager/night-audit" },
          { name: "Settlement",       icon: CreditCard,     path: "/dashboard/manager/settlement" },
          { name: "Shift Handover",   icon: FileText,       path: "/dashboard/manager/shift-handover" },
          { name: "Guest Journey",    icon: Map,            path: "/dashboard/manager/guest-journey" },
        ],
      },

      {
        name: "Live Chat", icon: MessageSquare, path: "/dashboard/manager/chat", section: "Messaging",
        children: [
          { name: "Live Chat",      icon: MessageSquare, path: "/dashboard/manager/chat" },
        ],
      },

      { name: "Settings", icon: Settings, path: "/dashboard/manager/settings", section: "Hotel" },
    ],

    /* ── ROOM SERVICE (staff) ── */
    room_service: [
      { name: "Live Orders",  icon: Package, path: "/dashboard/roomservice?tab=orders",     section: "Room Service" },
      { name: "New Order",    icon: Plus,    path: "/dashboard/roomservice?tab=new-order" },
      { name: "Kitchen View", icon: ChefHat, path: "/dashboard/roomservice/kitchen" },
    ],

    /* ── ROOM SERVICE MANAGER ── */
    room_service_manager: [
      { name: "Dashboard",    icon: Home,    path: "/dashboard/roomservice/manager?tab=analytics", section: "Overview" },

      {
        name: "Orders", icon: Package, path: "/dashboard/roomservice/manager?tab=orders", section: "Operations",
        children: [
          { name: "Live Orders",   icon: Package, path: "/dashboard/roomservice/manager?tab=orders" },
          { name: "New Order",     icon: Plus,    path: "/dashboard/roomservice/manager?tab=new-order" },
          { name: "Order History", icon: History, path: "/dashboard/roomservice/manager?tab=history" },
          { name: "Kitchen View",  icon: ChefHat, path: "/dashboard/roomservice/kitchen" },
        ],
      },

      { name: "Menu",          icon: UtensilsCrossed, path: "/dashboard/roomservice/manager?tab=menu",      section: "Management" },
      { name: "Analytics",     icon: BarChart3,        path: "/dashboard/roomservice/manager?tab=analytics", section: "Analytics" },
      { name: "Staff Attendance", icon: Clock,         path: "/dashboard/manager/staff-attendance",           section: "Staff" },
    ],

    /* ── SPA (staff) ── */
    spa: [
      { name: "Overview",     icon: Home,        path: "/dashboard/spa",                      section: "Spa" },
      { name: "Calendar",     icon: CalendarDays, path: "/dashboard/spa?tab=calendar" },
      { name: "Appointments", icon: Clock,        path: "/dashboard/spa?tab=appointments" },
      { name: "Services",     icon: Sparkles,     path: "/dashboard/spa?tab=services" },
    ],

    /* ── SPA MANAGER ── */
    spa_manager: [
      { name: "Overview",     icon: Home,        path: "/dashboard/spa",                         section: "Spa" },
      { name: "Calendar",     icon: CalendarDays, path: "/dashboard/spa?tab=calendar" },
      { name: "Appointments", icon: Clock,        path: "/dashboard/spa?tab=appointments" },
      { name: "Services",     icon: Sparkles,     path: "/dashboard/spa?tab=services",           section: "Management" },
      { name: "Staff",        icon: Users,        path: "/dashboard/spa?tab=staff" },
      { name: "Analytics",    icon: BarChart3,    path: "/dashboard/spa?tab=analytics",          section: "Analytics" },
      { name: "Staff Attendance", icon: Clock,    path: "/dashboard/manager/staff-attendance",   section: "Staff" },
    ],

    /* ── HOUSEKEEPER (staff) ── */
    housekeeper: [
      { name: "Housekeeping", icon: ClipboardCheck, path: "/dashboard/housekeeping", section: "Housekeeping" },
    ],

    /* ── HOUSEKEEPER MANAGER ── */
    housekeeper_manager: [
      { name: "Overview",        icon: Home,           path: "/dashboard/housekeeping/manager",     section: "Housekeeping" },
      { name: "All Rooms",       icon: ClipboardCheck, path: "/dashboard/housekeeping" },
      { name: "Staff Attendance",icon: Clock,          path: "/dashboard/manager/staff-attendance", section: "Staff" },
    ],
  };

  menus.manager   = menus.admin;
  menus.reception = menus.frontdesk;

  /* ── LIVECHAT (Fiord) ── */
  menus.livechat = [
    { name: "Guests",    icon: Users,         path: "/dashboard/manager/guests", section: "Guest Management" },
    { name: "Live Chat", icon: MessageSquare, path: "/dashboard/manager/chat",   section: "Messaging" },
  ];

  const userMenu  = menus[role || ""] || [];
  const roleMeta  = ROLE_META[role || ""] || { label: role || "Staff", color: "rgba(242,235,217,0.4)", bg: "rgba(100,116,139,0.18)" };
  const initials  = name ? name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() : "?";

  const toggle = (name: string) =>
    setOpenMenus(o => ({ ...o, [name]: !o[name] }));

  return (
    <aside className={`sidebar${drawerOpen ? " sidebar-open" : ""}`}>
      {/* ── Header ── */}
      <div className="sidebar-header">
        {logo
          ? <img src={logo} alt="Hotel Logo" className="hotel-logo" />
          : <div className="hotel-logo-placeholder">AI²</div>
        }
        <div className="sidebar-user">
          <div className="sidebar-avatar" style={{ background: roleMeta.bg, color: roleMeta.color }}>
            {initials}
          </div>
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{name || "Staff"}</span>
            <span className="sidebar-role-badge" style={{ background: roleMeta.bg, color: roleMeta.color }}>
              {roleMeta.label}
            </span>
          </div>
        </div>
      </div>

      {/* ── Nav ── */}
      <nav className="sidebar-nav">
        {userMenu.map((item, idx) => {
          const hasChildren = (item.children?.length ?? 0) > 0;
          const isOpen      = !!openMenus[item.name];
          const childActive = hasChildren && item.children!.some(c => isActive(c.path));
          const active      = !hasChildren && isActive(item.path);

          return (
            <div key={`${item.name}-${idx}`}>
              {item.section && (
                <div className="sidebar-section">{item.section}</div>
              )}

              {hasChildren ? (
                <div className="sidebar-group">
                  <button
                    className={`sidebar-link ${childActive ? "active" : ""}`}
                    onClick={() => toggle(item.name)}
                  >
                    <div className="indicator" />
                    <item.icon size={16} />
                    <span>{item.name}</span>
                    <ChevronDown size={12} className={`sidebar-chevron ${isOpen ? "open" : ""}`} />
                  </button>

                  {isOpen && (
                    <div className="sidebar-children">
                      {item.children!.map((child) => (
                        <button
                          key={child.path}
                          className={`sidebar-child-link ${isActive(child.path) ? "active" : ""}`}
                          onClick={() => go(child.path)}
                        >
                          <child.icon size={13} />
                          <span>{child.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  className={`sidebar-link ${active ? "active" : ""}`}
                  onClick={() => go(item.path)}
                >
                  <div className="indicator" />
                  <item.icon size={16} />
                  <span>{item.name}</span>
                </button>
              )}
            </div>
          );
        })}
      </nav>

      {/* ── Footer ── */}
      <div className="sidebar-footer">
        <button className="sidebar-logout" onClick={logout}>
          <LogOut size={15} /> Logout
        </button>
      </div>
    </aside>
  );
}
